<template>
    <Modal :cancel="cancel"
           class="order-payments-modal"
           cancel-label="CLOSE"
           icon="fa-coins"
           title="ORDER_PAYMENTS.MODAL_TITLE">
        <table class="table table-striped table-hover">
            <thead>
                <tr>
                    <th>{{ l('ORDER_PAYMENTS.PAID_AT') }}</th>
                    <th>{{ l('ORDER_PAYMENTS.PAYMENT_METHOD') }}</th>
                    <th>{{ l('ORDER_PAYMENTS.AMOUNT') }}</th>
                    <th colspan="2">
                        {{ l('ORDER_PAYMENTS.STATUS') }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="payment in payments"
                    :key="payment.paymentID"
                    @click="showPaymentDetails(payment)">
                    <td>
                        <span v-if="payment.takenAt">
                            {{ dateFilter(payment.takenAt, true) }}
                        </span>
                        <span v-if="!payment.takenAt && payment.confirmedAt">
                            {{ dateFilter(payment.confirmedAt, true) }}
                        </span>
                    </td>
                    <td>{{ payment.paymentMethod.shortName }}</td>
                    <td>{{ currencyFilter(payment.amount) }}</td>
                    <td><PaymentStatusLabel :payment="payment" /></td>
                    <td><i class="fa-solid fa-magnifying-glass"></i></td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <th colspan="2">
                        {{ l('PAYMENT_STATUS.PENDING') }}:
                    </th>
                    <th colspan="2">
                        {{ currencyFilter(pendingPayments) }}
                    </th>
                </tr>
                <tr>
                    <th colspan="2">
                        {{ l('PAYMENT_STATUS.SETTLED') }}:
                    </th>
                    <th colspan="2">
                        {{ currencyFilter(settledPayments) }}
                    </th>
                </tr>
                <tr>
                    <th colspan="2">
                        {{ l('PAYMENT_STATUS.FAILED') }}:
                    </th>
                    <th colspan="2">
                        {{ currencyFilter(failedPayments) }}
                    </th>
                </tr>
                <tr>
                    <th colspan="2">
                        {{ l('PAYMENT_STATUS.PAID') }}:
                    </th>
                    <th colspan="2">
                        {{ currencyFilter(completedPayments) }}
                    </th>
                </tr>
                <tr>
                    <th colspan="2">
                        {{ l('ORDER_PAYMENTS.BALANCE') }}:
                    </th>
                    <th colspan="2">
                        {{ currencyFilter(outstandingBalance) }}
                    </th>
                </tr>
            </tfoot>
        </table>
    </Modal>
</template>

<script>
import DateFormat from '@/lib/Dates/DateFormat';
import Modal from '@/vue/components/Modals/Modal';
import PaymentStatusLabel from '@/vue/components/Badges/PaymentStatusLabel';
import { container } from '@/container';
import {
    getOutstandingBalanceOfOrder,
    getTotalOfCompletedPayments,
    getTotalOfFailedPayments,
    getTotalOfPendingPayments,
    getTotalOfSettledPayments,
} from '@/lib/Payments/paymentHelperFunctions';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'OrderPaymentsModal',

    components: { PaymentStatusLabel, Modal },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        payments: {
            type: Array,
            required: true,
        },
        orderTotal: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            DateFormat,
        };
    },

    computed: {
        completedPayments() {
            return getTotalOfCompletedPayments(this.payments);
        },
        failedPayments() {
            return getTotalOfFailedPayments(this.payments);
        },
        settledPayments() {
            return getTotalOfSettledPayments(this.payments);
        },
        pendingPayments() {
            return getTotalOfPendingPayments(this.payments);
        },
        outstandingBalance() {
            return getOutstandingBalanceOfOrder(this.orderTotal, this.completedPayments);
        },
    },

    methods: {
        /**
         * @param {Payment} payment
         */
        async showPaymentDetails(payment) {
            await container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.ORDER_PAYMENT_DETAILS,
                {
                    payment,
                },
            );
        },
    },
};
</script>

<style lang="less">
.order-payments-modal {
    tbody {
        tr {
            cursor: pointer;
        }

        tr:last-child {
            td {
                border-bottom: 1px solid transparent;
            }
        }
    }

    tfoot {
        tr:first-child th {
            padding-top: 40px;
            vertical-align: bottom;
        }
    }
}
</style>
